import {
  Component,
  AfterViewInit,
  OnInit,
  OnChanges,
  SimpleChanges,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
} from "@angular/core";
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatOptionModule } from "@angular/material/core";
import { MatDividerModule } from "@angular/material/divider";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatRadioModule } from "@angular/material/radio";
import { MatSelectModule } from "@angular/material/select";
import { MatTimepickerModule } from "@angular/material/timepicker";
import { provideNativeDateAdapter } from "@angular/material/core";
import { VenueService } from "../../../shared/services/venue.service";
import { PolicyTemplateService } from "../../../shared/services/policy-template.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { CommonModule } from "@angular/common";
import {
  VenueLocationPickerComponent,
  VenueLocationValue,
} from "./venue-location-picker.component";
import {
  isOvernightVenueTiming,
  isVenueEndTimeDisabled,
  validateVenueTimings,
} from "./venue-timing";

@Component({
  selector: "app-venue",
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatOptionModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatRadioModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatTimepickerModule,
    MatProgressSpinnerModule,
    VenueLocationPickerComponent,
  ],
  templateUrl: "./venue.component.html",
  styleUrl: "./venue.component.scss",
  providers: [provideNativeDateAdapter()],
})
export class VenueComponent implements OnInit, OnChanges, AfterViewInit {
  venueForm: UntypedFormGroup;

  isLoading = false;
  isEditMode = false;

  location: VenueLocationValue | null = null;

  times: string[] = Array.from(
    { length: 25 },
    (_, i) => i.toString().padStart(2, "0") + ":00",
  );

  // Days of week for timing
  timing = [
    { name: "Sunday", checked: false, startTime: "", endTime: "" },
    { name: "Monday", checked: false, startTime: "", endTime: "" },
    { name: "Tuesday", checked: false, startTime: "", endTime: "" },
    { name: "Wednesday", checked: false, startTime: "", endTime: "" },
    { name: "Thursday", checked: false, startTime: "", endTime: "" },
    { name: "Friday", checked: false, startTime: "", endTime: "" },
    { name: "Saturday", checked: false, startTime: "", endTime: "" },
  ];

  // Cancellation policy
  cancellationPolicy = {
    enabled: false,
    rules: [{ hoursBeforeBooking: 24, refundPercentage: 50 }],
    defaultRefundPercentage: 0,
  };

  // Selected templates for tracking dropdowns
  selectedCancellationTemplate: any = null;
  selectedPrivacyPolicyTemplate: any = null;

  // Templates loaded from API
  cancellationTemplates: any[] = [];
  privacyPolicyTemplates: any[] = [];
  isLoadingTemplates = false;

  errors: string[] = [];

  private _data: any;

  @Input()
  set data(value: any) {
    this._data = value;
    console.log("Data setter called with:", value);
    if (value && value !== null && value !== undefined) {
      // Data is available, load it after a brief delay to ensure form is ready
      if (this.venueForm) {
        setTimeout(() => {
          this.loadVenueData(value);
          // After loading venue data, try to match templates if they're already loaded
          if (
            this.cancellationTemplates.length > 0 ||
            this.privacyPolicyTemplates.length > 0
          ) {
            setTimeout(() => {
              this.matchExistingPolicies();
            }, 100);
          }
        }, 10);
      } else {
        // Form not ready yet, will be loaded in ngOnInit
      }
    }
    this.cdr.markForCheck();
  }

  get data(): any {
    return this._data;
  }

  @Output() clicked = new EventEmitter<string>();

  constructor(
    private _formBuilder: UntypedFormBuilder,
    private venueService: VenueService,
    private policyTemplateService: PolicyTemplateService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
  ) {
    this.loadTemplates();
  }

  /**
   * Load templates from API
   */
  loadTemplates(): void {
    this.isLoadingTemplates = true;

    // Track if both templates are loaded to trigger matching
    let cancellationLoaded = false;
    let privacyLoaded = false;

    const checkAndMatch = () => {
      if (cancellationLoaded && privacyLoaded) {
        // Both templates are loaded, now try to match if venue data is already loaded
        if (this.data && this.data !== null && this.data !== undefined) {
          this.matchExistingPolicies();
        }
      }
    };

    // Load cancellation templates
    this.policyTemplateService.getCancellationTemplates().subscribe({
      next: (templates) => {
        this.cancellationTemplates = (templates || []).map((t: any) => ({
          name: t.name,
          value: t.policy,
          id: t._id || t.id,
          isDefault: t.isDefault,
        }));
        cancellationLoaded = true;
        this.isLoadingTemplates = false;
        checkAndMatch();
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error("Error loading cancellation templates:", err);
        this.cancellationTemplates = [];
        cancellationLoaded = true;
        this.isLoadingTemplates = false;
        checkAndMatch();
        this.cdr.markForCheck();
      },
    });

    // Load privacy templates
    this.policyTemplateService.getPrivacyTemplates().subscribe({
      next: (templates) => {
        this.privacyPolicyTemplates = (templates || []).map((t: any) => ({
          name: t.name,
          value: t.content,
          id: t._id || t.id,
          isDefault: t.isDefault,
        }));
        privacyLoaded = true;
        checkAndMatch();
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error("Error loading privacy templates:", err);
        this.privacyPolicyTemplates = [];
        privacyLoaded = true;
        checkAndMatch();
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Match existing policies to templates after templates are loaded
   */
  private matchExistingPolicies(): void {
    // Match cancellation policy if it exists
    if (
      this.cancellationPolicy &&
      this.cancellationPolicy.enabled &&
      this.cancellationPolicy.rules.length > 0
    ) {
      const matchedTemplate = this.matchCancellationTemplate(
        this.cancellationPolicy,
      );
      if (matchedTemplate) {
        this.selectedCancellationTemplate = matchedTemplate;
        console.log("Matched cancellation template:", matchedTemplate.name);
        this.cdr.markForCheck();
      } else {
        console.log("No matching cancellation template found");
      }
    }

    // Match privacy policy if it exists
    const privacyPolicyText = this.venueForm.get("privacyPolicy")?.value;
    if (privacyPolicyText) {
      const matchedTemplate =
        this.matchPrivacyPolicyTemplate(privacyPolicyText);
      if (matchedTemplate) {
        this.selectedPrivacyPolicyTemplate = matchedTemplate;
        console.log("Matched privacy template:", matchedTemplate.name);
        this.cdr.markForCheck();
      } else {
        console.log("No matching privacy template found");
      }
    }
  }

  /**
   * Compare function for mat-select to match templates by ID
   */
  compareTemplates(template1: any, template2: any): boolean {
    if (!template1 || !template2) return template1 === template2;
    return (template1.id || template1._id) === (template2.id || template2._id);
  }

  /**
   * On init - initialize form with defaults
   */
  ngOnInit(): void {
    // Initialize with default form (will be populated when data arrives)
    this.venueForm = this._formBuilder.group({
      name: ["", Validators.required],
      status: ["active", Validators.required],
      timing: [{}, Validators.required],
      location: ["", Validators.required],
      privacyPolicy: [""],
      rules: [""],
    });

    // Reset cancellation policy to defaults (empty - must select template)
    this.cancellationPolicy = {
      enabled: false,
      rules: [],
      defaultRefundPercentage: 0,
    };

    // Load data if it's already available (using setTimeout to ensure form is ready)
    if (this.data && this.data !== null && this.data !== undefined) {
      setTimeout(() => {
        console.log("Loading data in ngOnInit");
        this.loadVenueData(this.data);
      }, 50);
    }
    this.cdr.markForCheck();
  }

  /**
   * Handle input changes - when data arrives asynchronously
   */
  ngOnChanges(changes: SimpleChanges): void {
    console.log("ngOnChanges triggered:", changes);

    // Ensure form is initialized
    if (!this.venueForm) {
      this.venueForm = this._formBuilder.group({
        name: ["", Validators.required],
        status: ["active", Validators.required],
        timing: [{}, Validators.required],
        location: ["", Validators.required],
        privacyPolicy: [""],
        rules: [""],
      });
    }

    if (changes["data"]) {
      const currentValue = changes["data"].currentValue;

      if (currentValue && currentValue !== null && currentValue !== undefined) {
        // Data is available, load it
        console.log("Loading venue data from ngOnChanges");
        setTimeout(() => {
          this.loadVenueData(currentValue);
          // After loading venue data, try to match templates if they're already loaded
          if (
            this.cancellationTemplates.length > 0 ||
            this.privacyPolicyTemplates.length > 0
          ) {
            setTimeout(() => {
              this.matchExistingPolicies();
            }, 100);
          }
        }, 0);
      } else {
        // Reset to new venue mode
        console.log("Resetting to new venue mode");
        this.isEditMode = false;
        this.location = null;
        if (this.venueForm) {
          this.venueForm.patchValue({
            name: "",
            status: "active",
            timing: {},
            location: "",
            privacyPolicy: "",
          });
        }
        this.cancellationPolicy = {
          enabled: false,
          rules: [{ hoursBeforeBooking: 24, refundPercentage: 50 }],
          defaultRefundPercentage: 0,
        };
      }
    }
    this.cdr.markForCheck();
  }

  /**
   * Load venue data into form
   */
  private loadVenueData(data: any): void {
    if (!data || data === null || data === undefined) {
      return;
    }

    // Ensure form is initialized
    if (!this.venueForm) {
      this.venueForm = this._formBuilder.group({
        name: ["", Validators.required],
        status: ["active", Validators.required],
        timing: [{}, Validators.required],
        location: ["", Validators.required],
        privacyPolicy: [""],
        rules: [""],
      });
    }

    this.isEditMode = true;

    // Load timing - ensure we have the default structure
    if (data.timing && Array.isArray(data.timing) && data.timing.length > 0) {
      this.timing = [...data.timing];
      console.log("Timing loaded:", this.timing);
    } else if (data.timing && typeof data.timing === "object") {
      // If timing is an object, try to preserve it
      this.timing = data.timing;
    }
    // If no timing data, keep the default timing array initialized in class

    // Load cancellation policy if it exists
    if (
      data.cancellationPolicy &&
      typeof data.cancellationPolicy === "object"
    ) {
      this.cancellationPolicy = {
        enabled: data.cancellationPolicy.enabled === true,
        rules:
          data.cancellationPolicy.rules &&
          Array.isArray(data.cancellationPolicy.rules)
            ? [...data.cancellationPolicy.rules] // Copy array to avoid reference issues
            : [],
        defaultRefundPercentage:
          data.cancellationPolicy.defaultRefundPercentage !== undefined
            ? data.cancellationPolicy.defaultRefundPercentage
            : 0,
      };

      // Try to match the policy to a template (only if templates are already loaded)
      if (this.cancellationTemplates.length > 0) {
        this.selectedCancellationTemplate = this.matchCancellationTemplate(
          this.cancellationPolicy,
        );
      }
      // If templates aren't loaded yet, matching will happen in matchExistingPolicies() after templates load

      // If no template matches but policy exists, still show it (it might be a custom policy from before)
      // But user won't be able to edit it - they'll need to select a template
    } else {
      // No cancellation policy exists
      this.cancellationPolicy = {
        enabled: false,
        rules: [],
        defaultRefundPercentage: 0,
      };
      this.selectedCancellationTemplate = null;
    }

    // Try to match privacy policy to a template (only if templates are already loaded)
    if (data.privacyPolicy) {
      if (this.privacyPolicyTemplates.length > 0) {
        this.selectedPrivacyPolicyTemplate = this.matchPrivacyPolicyTemplate(
          data.privacyPolicy,
        );
      }
      // If templates aren't loaded yet, matching will happen in matchExistingPolicies() after templates load
    } else {
      this.selectedPrivacyPolicyTemplate = null;
    }

    // Update form values
    const formValues = {
      name: data.name || "",
      status: data.status || "active",
      timing: data.timing || {},
      location: data.location?.placeName || data.location?.placeAddress || "",
      privacyPolicy: data.privacyPolicy || "",
      rules: data.rules || "",
    };

    this.venueForm.patchValue(formValues, { emitEvent: false });

    // Also use setValue for critical fields to ensure they update
    if (data.name) {
      this.venueForm.get("name")?.setValue(data.name, { emitEvent: false });
    }
    if (data.status) {
      this.venueForm.get("status")?.setValue(data.status, { emitEvent: false });
    }
    if (data.privacyPolicy) {
      this.venueForm
        .get("privacyPolicy")
        ?.setValue(data.privacyPolicy, { emitEvent: false });
    }

    // Set location data
    if (data.location) {
      this.location = this.toVenueLocationValue(data.location);
      if (!this.location) {
        this.venueForm.get("location")?.setValue("", { emitEvent: false });
      }
      console.log("Location set:", this.location);
    } else {
      this.location = null;
      this.venueForm.get("location")?.setValue("", { emitEvent: false });
    }

    // Force change detection
    this.cdr.detectChanges();
    console.log("=== FINISHED LOADING VENUE DATA ===");
  }

  ngAfterViewInit(): void {
    if (this.data && this.data !== null && this.data !== undefined) {
      const formName = this.venueForm?.get("name")?.value;
      if (!formName || formName === "") {
        setTimeout(() => {
          this.loadVenueData(this.data);
        }, 0);
      }
    }

    this.cdr.markForCheck();
  }

  onLocationChange(location: VenueLocationValue | null): void {
    this.location = location;
    const locationControl = this.venueForm.get("location");
    const displayValue = location
      ? location.placeAddress || location.placeName
      : "";
    locationControl?.setValue(displayValue);
    locationControl?.markAsDirty();
    locationControl?.markAsTouched();
    locationControl?.updateValueAndValidity();
    this.errors = this.errors.filter(
      (error) => error !== "Select and confirm the venue location",
    );
    this.cdr.markForCheck();
  }

  hasUnsavedChanges(): boolean {
    return this.venueForm?.dirty === true;
  }

  applyToAll() {
    const firstDay = this.timing[0];
    if (firstDay.checked) {
      this.timing.forEach((day, index) => {
        if (index !== 0) {
          day.checked = true;
          day.startTime = firstDay.startTime;
          day.endTime = firstDay.endTime;
        }
      });
    }
    this.cdr.markForCheck();
  }

  startTimeChange(startTime: string, endTime: string, index: number): void {
    if (startTime === endTime) {
      this.timing[index].endTime = "";
    }
    this.cdr.markForCheck();
  }

  isDisabled(startTime: string, endTime: string): boolean {
    return isVenueEndTimeDisabled(startTime, endTime);
  }

  isNextDayEndTime(startTime: string, endTime: string): boolean {
    return isOvernightVenueTiming(startTime, endTime);
  }

  validateTimings(timings: any[]): string[] {
    return validateVenueTimings(timings);
  }
  /**
   * Apply cancellation policy template
   */
  applyCancellationTemplate(template: any): void {
    if (template && template.value) {
      this.cancellationPolicy = {
        enabled:
          template.value.enabled !== undefined ? template.value.enabled : true,
        rules: template.value.rules
          ? JSON.parse(JSON.stringify(template.value.rules))
          : [],
        defaultRefundPercentage:
          template.value.defaultRefundPercentage !== undefined
            ? template.value.defaultRefundPercentage
            : 0,
      };
      this.selectedCancellationTemplate = template;
      // Ensure policy is enabled when template is selected
      this.cancellationPolicy.enabled = true;
    } else {
      this.selectedCancellationTemplate = null;
      this.cancellationPolicy = {
        enabled: this.cancellationPolicy.enabled,
        rules: [],
        defaultRefundPercentage: 0,
      };
    }
    this.cdr.markForCheck();
  }

  /**
   * Handle cancellation policy enable/disable toggle
   */
  onCancellationPolicyToggle(): void {
    if (!this.cancellationPolicy.enabled) {
      // If disabling, clear template selection
      this.selectedCancellationTemplate = null;
      this.cancellationPolicy.rules = [];
      this.cancellationPolicy.defaultRefundPercentage = 0;
    }
    // If enabling, user must select a template (handled by UI)
    this.cdr.markForCheck();
  }

  /**
   * Match cancellation policy to a template
   */
  private matchCancellationTemplate(policy: any): any {
    if (!policy || !policy.enabled) {
      return null;
    }

    // Sort rules by hoursBeforeBooking descending for comparison
    const policyRules = [...(policy.rules || [])].sort(
      (a, b) => b.hoursBeforeBooking - a.hoursBeforeBooking,
    );
    const policyDefaultRefund = policy.defaultRefundPercentage || 0;

    for (const template of this.cancellationTemplates) {
      if (!template.value || !template.value.enabled) continue;

      const templateRules = [...(template.value.rules || [])].sort(
        (a, b) => b.hoursBeforeBooking - a.hoursBeforeBooking,
      );
      const templateDefaultRefund = template.value.defaultRefundPercentage || 0;

      // Check if rules match
      if (policyRules.length !== templateRules.length) continue;
      if (policyDefaultRefund !== templateDefaultRefund) continue;

      let matches = true;
      for (let i = 0; i < policyRules.length; i++) {
        if (
          policyRules[i].hoursBeforeBooking !==
            templateRules[i].hoursBeforeBooking ||
          policyRules[i].refundPercentage !== templateRules[i].refundPercentage
        ) {
          matches = false;
          break;
        }
      }

      if (matches) {
        return template;
      }
    }

    return null;
  }

  /**
   * Apply privacy policy template
   */
  applyPrivacyPolicyTemplate(template: any): void {
    if (template && template.value) {
      this.venueForm.patchValue({
        privacyPolicy: template.value,
      });
      this.selectedPrivacyPolicyTemplate = template;
    } else {
      this.selectedPrivacyPolicyTemplate = null;
    }
    this.cdr.markForCheck();
  }

  /**
   * Match privacy policy to a template
   */
  private matchPrivacyPolicyTemplate(policyText: string): any {
    if (!policyText || !policyText.trim()) {
      return null;
    }

    // Normalize whitespace for comparison
    const normalizedPolicy = policyText.trim().replace(/\s+/g, " ");

    for (const template of this.privacyPolicyTemplates) {
      if (!template.value) continue;

      const normalizedTemplate = template.value.trim().replace(/\s+/g, " ");

      // Check if texts match (exact match)
      if (normalizedPolicy === normalizedTemplate) {
        return template;
      }
    }

    return null; // No template matches
  }

  /**
   * Submit the form to create venue
   */
  onSubmit(): void {
    this.errors = this.validateTimings(this.timing);
    if (!this.location?.markerPos) {
      this.errors.push("Select and confirm the venue location");
    }
    if (this.venueForm.invalid || this.isLoading || this.errors.length > 0) {
      this.cdr.markForCheck();
      return;
    }

    this.isLoading = true;
    this.cdr.markForCheck();
    const venueId = this.data?.id || this.data?._id;

    const venueData: any = {
      id: venueId || undefined,
      name: this.venueForm.value.name,
      status: this.venueForm.value.status,
      location: {
        ...this.location!,
        placeName: String(this.venueForm.value.name).trim(),
      },
      timing: this.timing,
      rules: this.venueForm.value.rules,
    };
    venueData.cancellationPolicy = {
      enabled: this.cancellationPolicy.enabled,
      rules: this.cancellationPolicy.rules || [],
      defaultRefundPercentage:
        this.cancellationPolicy.defaultRefundPercentage !== undefined
          ? this.cancellationPolicy.defaultRefundPercentage
          : 0,
    };
    venueData.privacyPolicy = this.venueForm.value.privacyPolicy || "";

    this.venueService.createOrUpdateVenue(venueData).subscribe({
      next: (response) => {
        this.isLoading = false;
        const message = this.isEditMode
          ? "Venue updated successfully!"
          : "Venue created successfully!";
        this.snackBar.open(message, "Close", {
          duration: 3000,
          panelClass: ["success-snackbar"],
        });
        this.clicked.emit("Hello from Child!");
        this.venueForm.markAsPristine();
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.isLoading = false;
        const message = this.isEditMode
          ? "Error updating venue. Please try again."
          : "Error creating venue. Please try again.";
        this.snackBar.open(message, "Close", {
          duration: 3000,
          panelClass: ["error-snackbar"],
        });
        console.error("Error saving venue:", error);
        this.cdr.markForCheck();
      },
    });
  }

  private toVenueLocationValue(location: any): VenueLocationValue | null {
    const lat = Number(location?.markerPos?.lat);
    const lng = Number(location?.markerPos?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return null;
    }

    return {
      markerPos: { lat, lng },
      placeAddress: String(location?.placeAddress ?? ""),
      placeId: String(location?.placeId ?? ""),
      placeName: String(location?.placeName ?? ""),
    };
  }
}
